import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast, Toaster } from "sonner";
import { Flag, ArrowLeft, Warehouse, MagnifyingGlass, Plus } from "@phosphor-icons/react";

export function HeadCounterDashboard() {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/sessions")
      .then(({ data }) => setSessions(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  return (
    <AppShell>
      <Toaster position="top-right" richColors />
      <div className="max-w-4xl mx-auto p-6 space-y-5">
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-zinc-500 font-semibold">Head counter</div>
          <h1 className="font-display text-3xl font-bold text-zinc-900 tracking-tight">Head Counter Sessions</h1>
          <p className="text-sm text-zinc-600">Review counter counts and finalize unresolved SKUs.</p>
        </div>

        <div className="grid gap-3">
          {sessions.map((s) => (
            <Link
              key={s.id}
              to={`/head-counter/sessions/${s.id}`}
              className="rounded-sm border border-zinc-300 bg-white p-4 flex items-center justify-between hover:border-indigo-400"
            >
              <div>
                <div className="font-display font-semibold text-zinc-900">{s.session_name}</div>
                <div className="text-xs text-zinc-500 mono">
                  {s.warehouse_name || "—"} · <span className="uppercase">{s.session_status}</span>
                </div>
              </div>
              <div className="text-sm text-indigo-900 inline-flex items-center gap-1">
                Open <Flag className="w-4 h-4" />
              </div>
            </Link>
          ))}

          {sessions.length === 0 && (
            <div className="rounded-sm border border-zinc-300 bg-white p-10 text-center text-zinc-500">
              No sessions assigned.
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

export function HeadCounterSession() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [supervisor, setSupervisor] = useState("");
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    final_head_count: "",
    final_reason: "",
    final_note: "",
    other_reason: "",
  });

  const [foundModal, setFoundModal] = useState(false);
  const [foundSaving, setFoundSaving] = useState(false);
  const [foundForm, setFoundForm] = useState({
    warehouse_code: "",
    item_code: "",
    item_description: "",
    size: "",
    location: "",
    tonality: "",
    pcs_per_box: "",
    price: "",
    counted_quantity: "",
    alley_supervisor: "",
    remarks: "",
  });

  const load = async () => {
    try {
      const { data } = await api.get(`/head-counter/sessions/${id}/items`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const openItem = (it) => {
    setSelected(it);
    const r = it.reconciliation || {};
    const savedReason = r.final_reason || "";
    setForm({
      final_head_count: r.final_head_count != null ? String(r.final_head_count) : "",
      final_reason: savedReason === "other" ? "other" : savedReason,
      other_reason: savedReason === "other" ? (r.final_note || "") : "",
      final_note: savedReason === "other" ? "" : (r.final_note || ""),
    });
  };

  const finalize = async () => {
    if (!selected) return;

    const n = parseInt(form.final_head_count, 10);
    if (isNaN(n) || n < 0) {
      toast.error("Enter a valid whole number");
      return;
    }

    if (!form.final_reason) {
      toast.error("Please choose a reason");
      return;
    }

    if (form.final_reason === "other" && !form.other_reason.trim()) {
      toast.error("Please enter the other reason");
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/head-counter/sessions/${id}/items/${selected.id}/finalize`, {
        final_head_count: n,
        final_reason: form.final_reason,
        final_note: form.final_reason === "other" ? form.other_reason.trim() : form.final_note,
      });

      toast.success("Item finalized");
      setSelected(null);
      setForm({ final_head_count: "", final_reason: "", final_note: "", other_reason: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const resetFoundForm = () => {
    setFoundForm({
      warehouse_code: "",
      item_code: "",
      item_description: "",
      size: "",
      location: "",
      tonality: "",
      pcs_per_box: "",
      price: "",
      counted_quantity: "",
      alley_supervisor: "",
      remarks: "",
    });
  };

  const addFoundItem = async () => {
    const qty = parseInt(foundForm.counted_quantity || "0", 10);
    if (isNaN(qty) || qty < 0) {
      toast.error("Enter a valid found quantity");
      return;
    }

    if (!foundForm.item_code.trim() && !foundForm.item_description.trim()) {
      toast.error("Enter at least an item code or description");
      return;
    }

    setFoundSaving(true);
    try {
      await api.post(`/head-counter/sessions/${id}/found-item`, {
        ...foundForm,
        counted_quantity: qty,
        pcs_per_box: foundForm.pcs_per_box === "" ? null : Number(foundForm.pcs_per_box),
        price: foundForm.price === "" ? null : Number(foundForm.price),
      });

      toast.success("Found item added");
      setFoundModal(false);
      resetFoundForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setFoundSaving(false);
    }
  };

  const allItems = useMemo(
    () => (Array.isArray(data?.items) ? data.items : []),
    [data]
  );
  const search = q.trim().toLowerCase();

  const supervisorOptions = useMemo(() => {
    const names = new Set();
    let unassigned = false;
    allItems.forEach((it) => {
      const s = (it.alley_supervisor || "").trim();
      if (s) names.add(s);
      else unassigned = true;
    });
    return {
      names: [...names].sort((a, b) => a.localeCompare(b)),
      unassigned,
    };
  }, [allItems]);

  const items = useMemo(() => {
    return allItems.filter((it) => {
      const sup = (it.alley_supervisor || "").trim();
      if (supervisor === "__unassigned__") {
        if (sup) return false;
      } else if (supervisor && sup !== supervisor) {
        return false;
      }

      if (!search) return true;
      const r = it.reconciliation || {};
      const countersText = (it.counters || [])
        .map((c) => `${c.full_name || ""} ${c.email || ""} ${c.latest_count ?? ""} ${c.remarks || ""}`)
        .join(" ");

      const haystack = [
        it.item_code,
        it.item_description,
        it.warehouse_code,
        it.size,
        it.location,
        it.tonality,
        it.alley_supervisor,
        it.pcs_per_box,
        r.status,
        r.final_head_count,
        r.final_reason,
        r.final_note,
        it.finalized_by_name,
        countersText,
      ].map((v) => (v == null ? "" : String(v))).join(" ").toLowerCase();

      return haystack.includes(search);
    });
  }, [allItems, search, supervisor]);

  const statusLabel = (it) => {
    const r = it.reconciliation || {};
    if (r.status === "finalized_by_head_counter" || r.final_head_count != null) return "Finalized";
    if ((it.counters || []).every((c) => c.latest_count == null)) return "No Count";
    return "Needs Recount";
  };

  const discrepancyLabel = (it) => {
    const r = it.reconciliation || {};
    const systemCount = Number(it.system_count || 0);

    const actualCount =
      r.final_head_count != null
        ? Number(r.final_head_count)
        : Number(r.collective_count || 0);

    if (actualCount < systemCount) return "Short";
    if (actualCount > systemCount) return "Over";
    return "Matched";
  };

  if (!data) {
    return (
      <AppShell>
        <Toaster position="top-right" richColors />
        <div className="p-10 text-zinc-500">Loading…</div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <Toaster position="top-right" richColors />

      <div className="max-w-[1500px] mx-auto p-3 sm:p-6 space-y-4">
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div>
            <Link to="/head-counter" className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-900">
              <ArrowLeft className="w-3.5 h-3.5" /> Back
            </Link>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-900 tracking-tight mt-1">
              {data.session?.session_name || "Head Counter Session"}
            </h1>
            <div className="text-sm text-zinc-600 mono">
              <Warehouse className="w-4 h-4 inline mr-1 text-zinc-500" />
              {data.session?.warehouse_name || "—"}
            </div>
          </div>

          <button
            onClick={() => setFoundModal(true)}
            className="inline-flex items-center gap-1 rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white px-3 py-2 text-xs"
          >
            <Plus className="w-4 h-4" /> Add Item
          </button>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white p-3">
          <div className="flex gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[240px]">
              <MagnifyingGlass className="w-4 h-4 text-zinc-400 absolute left-2 top-1/2 -translate-y-1/2" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search item code, description, location, tonality, counter, remarks..."
                className="w-full rounded-sm border border-zinc-300 pl-8 pr-3 py-2 text-sm"
              />
            </div>

            <select
              value={supervisor}
              onChange={(e) => setSupervisor(e.target.value)}
              className="rounded-sm border border-zinc-300 px-2 py-2 text-sm min-w-[190px]"
              data-testid="hc-supervisor-filter"
            >
              <option value="">All Alley Supervisors</option>
              {supervisorOptions.names.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
              {supervisorOptions.unassigned && (
                <option value="__unassigned__">— Unassigned —</option>
              )}
            </select>
          </div>

          <div className="text-xs text-zinc-500 mono mt-1">
            Showing {items.length} of {allItems.length}
          </div>
        </div>

        <div className="rounded-sm border border-zinc-300 bg-white overflow-hidden">
          <div className="overflow-x-auto max-h-[70vh]">
            <table className="data-table text-xs sm:text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-sm">
                <tr>
                  <th>Status / Code</th>
                  <th>Description</th>
                  <th>WH</th>
                  <th>Size</th>
                  <th>Location</th>
                  <th>Tonality</th>
                  <th>Alley Supervisor</th>
                  <th className="text-right">System Count</th>
                  <th>Counter Counts / Remarks</th>
                  <th className="text-right">Total Actual Count</th>
                  <th className="text-right">Final Count</th>
                  <th>Discrepancy</th>
                  <th>Reason / Note</th>
                  <th className="text-right">Action</th>
                </tr>
              </thead>

              <tbody>
                {items.map((it) => {
                  const r = it.reconciliation || {};
                  return (
                    <tr key={it.id}>
                      <td className="leading-tight">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                          {statusLabel(it)}
                        </div>
                        <div className="mono font-semibold mt-1">{it.item_code || "—"}</div>
                        <div className="text-[11px] text-zinc-600">TON: {it.tonality || "—"}</div>
                      </td>

                      <td className="min-w-[200px]">{it.item_description || "—"}</td>
                      <td className="mono">{it.warehouse_code || "—"}</td>
                      <td className="mono">{it.size || "—"}</td>
                      <td className="mono">{it.location || "—"}</td>
                      <td className="mono">{it.tonality || "—"}</td>

                      <td className="whitespace-nowrap">
                        {it.alley_supervisor || <span className="text-zinc-400">—</span>}
                      </td>

                      <td className="mono font-semibold text-right">
                        {it.system_count ?? 0}
                      </td>

                      <td className="min-w-[190px] leading-tight">
                        {(it.counters || []).length === 0 && (
                          <div className="text-zinc-400 text-[11px]">No counter data</div>
                        )}

                        {(it.counters || []).map((c) => (
                          <div key={c.user_id} className="text-[11px] py-px">
                            <span className="text-blue-700 font-semibold">{c.full_name || "Counter"}</span>
                            <span className="mono font-bold text-blue-700">
                              {" "}
                              {c.latest_count ?? "No Count"}
                            </span>
                            {c.remarks ? (
                              <span className="text-zinc-600"> · {c.remarks}</span>
                            ) : null}
                          </div>
                        ))}
                      </td>

                      <td className="mono font-semibold text-right">
                        {r.collective_count ?? 0}
                      </td>

                      <td className="mono font-semibold text-right leading-tight">
                        {r.final_head_count ?? "—"}
                        {it.finalized_by_name ? (
                          <div className="text-[10px] font-normal text-zinc-500 whitespace-nowrap">
                            by {it.finalized_by_name}
                          </div>
                        ) : null}
                      </td>

                      <td className="font-semibold">
                        {discrepancyLabel(it)}
                      </td>

                      <td className="text-xs min-w-[160px]">
                        {r.final_reason || "—"}
                        {r.final_note ? <div className="text-zinc-600 mt-1">{r.final_note}</div> : null}
                      </td>

                      <td className="text-right">
                        <button
                          onClick={() => openItem(it)}
                          className="rounded-sm bg-indigo-950 hover:bg-indigo-900 text-white text-xs px-3 py-1.5"
                        >
                          {r.final_head_count != null ? "Edit Final" : "Finalize"}
                        </button>
                      </td>
                    </tr>
                  );
                })}

                {items.length === 0 && (
                  <tr>
                    <td colSpan={14} className="text-center py-10 text-zinc-500">
                      {search || supervisor
                        ? "No items match your filters."
                        : "No items require Head Counter review."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 bg-zinc-950/60 flex items-center justify-center z-50 p-4"
          onClick={() => !submitting && setSelected(null)}
        >
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm border border-zinc-300 w-full max-w-lg p-5">
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">
              Head Counter Finalization
            </div>

            <div className="font-display text-xl font-bold mt-1 text-zinc-900">
              {selected.item_code || "—"} — {selected.item_description || "—"}
            </div>

            <div className="mono text-xs text-zinc-500 mt-1">
              {selected.warehouse_code || "—"} · {selected.size || "—"} · {selected.location || "—"} · {selected.tonality || "—"} · pcs/box: {selected.pcs_per_box ?? "—"}
            </div>

            {(selected.counters || []).length > 0 && (
              <div className="mt-4 rounded-sm border border-blue-100 bg-blue-50 p-3">
                <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold mb-2">
                  Counter Counts and Remarks
                </div>

                <ul className="text-xs space-y-2">
                  {(selected.counters || []).map((c) => (
                    <li key={c.user_id} className="border-b border-blue-100 pb-1 last:border-b-0">
                      <div className="flex justify-between text-blue-700">
                        <span className="font-semibold">{c.full_name || "Counter"}</span>
                        <span className="mono font-bold">{c.latest_count ?? "No Count"}</span>
                      </div>
                      <div className="text-zinc-600 mt-0.5">
                        Remarks: {c.remarks || "—"}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <label className="block mt-4 text-xs uppercase tracking-[0.08em] text-zinc-500 font-semibold">
              Final head count
            </label>
            <input
              autoFocus
              type="number"
              min={0}
              step={1}
              value={form.final_head_count}
              onChange={(e) => setForm({ ...form, final_head_count: e.target.value })}
              className="w-full mt-1 rounded-sm border-2 border-zinc-300 px-4 py-3 text-2xl font-mono focus:border-indigo-500 focus:outline-none"
            />

            <label className="block mt-3 text-xs uppercase tracking-[0.08em] text-zinc-500 font-semibold">
              Reason
            </label>
            <select
              value={form.final_reason}
              onChange={(e) => setForm({ ...form, final_reason: e.target.value })}
              className="w-full mt-1 rounded-sm border border-zinc-300 px-3 py-2 text-sm"
            >
              <option value="">Select a reason…</option>
              {(data.reasons || []).map((r) => <option key={r} value={r}>{r}</option>)}
            </select>

            {form.final_reason === "other" ? (
              <>
                <label className="block mt-3 text-xs uppercase tracking-[0.08em] text-zinc-500 font-semibold">
                  Other reason
                </label>
                <input
                  value={form.other_reason}
                  onChange={(e) => setForm({ ...form, other_reason: e.target.value })}
                  className="w-full mt-1 rounded-sm border border-zinc-300 px-3 py-2 text-sm"
                  placeholder="Type other reason"
                />
              </>
            ) : (
              <>
                <label className="block mt-3 text-xs uppercase tracking-[0.08em] text-zinc-500 font-semibold">
                  Note optional
                </label>
                <textarea
                  rows={2}
                  value={form.final_note}
                  onChange={(e) => setForm({ ...form, final_note: e.target.value })}
                  className="w-full mt-1 rounded-sm border border-zinc-300 px-3 py-2 text-sm"
                />
              </>
            )}

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setSelected(null)} className="rounded-sm border border-zinc-300 hover:bg-zinc-100 px-4 py-2 text-sm">
                Cancel
              </button>
              <button
                disabled={submitting}
                onClick={finalize}
                className="rounded-sm bg-indigo-950 hover:bg-indigo-900 disabled:opacity-60 text-white px-4 py-2 text-sm inline-flex items-center gap-2"
              >
                <Flag className="w-4 h-4" /> {submitting ? "Finalizing…" : "Finalize"}
              </button>
            </div>
          </div>
        </div>
      )}

      {foundModal && (
        <div className="fixed inset-0 bg-zinc-950/60 flex items-center justify-center z-50 p-4" onClick={() => !foundSaving && setFoundModal(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-sm border border-zinc-300 w-full max-w-lg p-5">
            <div className="text-xs uppercase tracking-[0.14em] text-zinc-500 font-semibold">Found item</div>
            <h3 className="font-display text-xl font-bold text-zinc-900 mt-1">Add item not in masterlist</h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-4">
              {[
                ["warehouse_code", "Warehouse"],
                ["item_code", "Item Code"],
                ["item_description", "Description"],
                ["size", "Size"],
                ["location", "Location"],
                ["tonality", "Tonality"],
                ["pcs_per_box", "pcs/box"],
                ["price", "Price"],
                ["counted_quantity", "Quantity Found"],
                ["alley_supervisor", "Alley Supervisor"],
              ].map(([key, label]) => (
                <input
                  key={key}
                  type={["pcs_per_box", "price", "counted_quantity"].includes(key) ? "number" : "text"}
                  className="rounded-sm border border-zinc-300 px-3 py-2 text-sm"
                  placeholder={label}
                  value={foundForm[key]}
                  onChange={(e) => setFoundForm({ ...foundForm, [key]: e.target.value })}
                />
              ))}
            </div>

            <textarea
              rows={2}
              className="w-full mt-2 rounded-sm border border-zinc-300 px-3 py-2 text-sm"
              placeholder="Remarks"
              value={foundForm.remarks}
              onChange={(e) => setFoundForm({ ...foundForm, remarks: e.target.value })}
            />

            <div className="mt-5 flex items-center justify-end gap-2">
              <button onClick={() => setFoundModal(false)} className="rounded-sm border border-zinc-300 hover:bg-zinc-100 px-4 py-2 text-sm">
                Cancel
              </button>
              <button
                disabled={foundSaving}
                onClick={addFoundItem}
                className="rounded-sm bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white px-4 py-2 text-sm"
              >
                {foundSaving ? "Saving…" : "Save Found Item"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
